import type { ReactNode } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { tokens } from '@/styles/tokens';

/**
 * The how-it-works hint lines, as Figma draws them in BOTH places that teach the
 * legend: the empty state under the heading, and the ⓘ tooltip beside the "Legend"
 * caption. One component because they are the same sentences with the same middot
 * bullets — `onDark` only swaps the ink for the tooltip's dark ground and lets the
 * bubble's own type through, rather than restyling the tooltip.
 *
 * Bullets are drawn by a `::before` rather than `list-style`, so the text keeps a
 * hanging indent: a wrapped second line aligns under the first word, not under
 * the bullet.
 */

const List = styled('ul', {
  shouldForwardProp: (prop) => prop !== 'onDark',
})<{ onDark?: boolean }>(({ onDark }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  textAlign: 'left',
  ...(onDark ? { color: 'inherit' } : { maxWidth: 280, color: tokens.dataAnalysis.textMuted }),
}));

const Item = styled('li', {
  shouldForwardProp: (prop) => prop !== 'onDark',
})<{ onDark?: boolean }>(({ onDark }) => ({
  display: 'flex',
  gap: 6,
  lineHeight: 1.4,
  // On the dark bubble the type is the tooltip's own; only the panel sets its own.
  ...(onDark ? {} : { fontSize: 12, fontWeight: 500 }),
  '&::before': {
    content: '"·"',
    flexShrink: 0,
    fontWeight: 700,
    // The bullet steps back so the sentence carries the weight.
    ...(onDark && { color: alpha('#fff', 0.6) }),
  },
}));

export interface DocumentationHintListProps {
  hints: ReactNode[];
  /** Render for a dark surface (the ⓘ tooltip bubble) instead of the panel. */
  onDark?: boolean;
}

export function DocumentationHintList({ hints, onDark }: DocumentationHintListProps) {
  return (
    <List onDark={onDark}>
      {hints.map((hint, index) => (
        // Hints are static copy in a fixed order, so the index is a stable key.
        <Item key={index} onDark={onDark}>
          {hint}
        </Item>
      ))}
    </List>
  );
}
